import { useEffect, useState } from "react";
import type { FormEvent, ReactNode } from "react";
import { SPAM_FOLDER, account, cancelled, passkeysSupported, serverFeatures } from "../account.ts";
import { messageOf } from "../store.ts";
import type { SignedIn } from "../store.ts";
import { MovedBanner, OldPasswordHint } from "./Move.tsx";

/** Head and foot of every page without sign-in (as in the sister apps, docs/01-architektur.md, Benutzerverwaltung). */
export function AuthPage({ children }: { children: ReactNode }) {
  return (
    <div className="auth">
      <MovedBanner signedIn={false} />
      <h1>
        <img src="/icons/apple-touch-icon.png" alt="" />
        SpeiseKarte
      </h1>
      <p className="muted tagline">Speisekarten in einer Minute: auswählen, Preise anpassen, fertig</p>
      {children}
      <footer className="muted">
        SpeiseKarte · Testbetrieb
        <LegalLinks />
      </footer>
    </div>
  );
}

/**
 * Imprint and privacy policy: plain pages the server delivers (public/impressum.html …), in a new
 * window so the app on the home screen stays where it was (as in the EinkaufsZettel).
 */
export function LegalLinks() {
  return (
    <span className="legal" data-testid="legal-links">
      <a href="/impressum" target="_blank" rel="noopener">
        Impressum
      </a>
      {" · "}
      <a href="/datenschutz" target="_blank" rel="noopener">
        Datenschutz
      </a>
      {" · "}
      <a href="/lizenzen" target="_blank" rel="noopener">
        Lizenzen
      </a>
    </span>
  );
}

/** The usual eye for showing a password; crossed out while it is shown. */
function Eye({ crossed }: { crossed: boolean }) {
  return (
    <svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7S2 12 2 12z" />
      <circle cx="12" cy="12" r="3" />
      {crossed && <path d="M3 3l18 18" />}
    </svg>
  );
}

const authMessage = (err: unknown) => {
  const m = messageOf(err);
  return m.startsWith("Keine Verbindung") ? "Keine Verbindung zum Server. Anmelden geht nur mit Netz." : m;
};
export { authMessage };

/** User name and password; or a passkey of this device without typing anything. */
export function Login({ onSignedIn }: { onSignedIn: (s: SignedIn) => void }) {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [shown, setShown] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<"form" | "passkey" | null>(null);
  const [signupOpen, setSignupOpen] = useState<boolean | null>(null);
  const [passkeysOn, setPasskeysOn] = useState(false);
  const [forgot, setForgot] = useState(false);

  useEffect(() => {
    serverFeatures().then(
      (f) => [setSignupOpen(f.open), setPasskeysOn(f.passkeys)],
      () => setSignupOpen(false),
    );
  }, []);

  async function submit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    if (!username.trim() || !password) return setError("Bitte gib Benutzername und Passwort ein.");
    setBusy("form");
    try {
      onSignedIn(await account.login(username.trim(), password));
    } catch (err) {
      setError(authMessage(err));
    } finally {
      setBusy(null);
    }
  }

  async function passkey() {
    setError(null);
    setBusy("passkey");
    try {
      onSignedIn(await account.loginWithPasskey());
    } catch (err) {
      if (!cancelled(err)) setError(authMessage(err));
    } finally {
      setBusy(null);
    }
  }

  if (forgot) return <Forgot who={username} onBack={() => setForgot(false)} />;
  return (
    <AuthPage>
      <div className="card">
        <h2>Anmelden</h2>
        <form noValidate onSubmit={submit}>
          <label>
            Benutzername
            <input value={username} onChange={(e) => setUsername(e.target.value)} autoComplete="username webauthn" autoCapitalize="none" autoCorrect="off" spellCheck={false} data-testid="username" />
          </label>
          <label>
            Passwort
            <span className="reveal">
              <input type={shown ? "text" : "password"} value={password} onChange={(e) => setPassword(e.target.value)} autoComplete="current-password" autoCapitalize="none" autoCorrect="off" spellCheck={false} data-testid="password" />
              <button type="button" className="link" onClick={() => setShown(!shown)} aria-pressed={shown} aria-label={shown ? "Passwort verbergen" : "Passwort anzeigen"} title={shown ? "Passwort verbergen" : "Passwort anzeigen"}>
                <Eye crossed={shown} />
              </button>
            </span>
          </label>
          {error && <p className="error-box">{error}</p>}
          <button className="primary" disabled={busy !== null} data-testid="login">
            {busy === "form" ? "Anmelden …" : "Anmelden"}
          </button>
        </form>
        <p className="forgot-link">
          <button type="button" className="link" onClick={() => setForgot(true)} data-testid="forgot">
            Passwort vergessen?
          </button>
        </p>
        {passkeysOn && passkeysSupported() && (
          <button className="wide" disabled={busy !== null} onClick={passkey} data-testid="passkey-login">
            {busy === "passkey" ? "Mit Passkey anmelden …" : "Mit Passkey anmelden"}
          </button>
        )}
        <OldPasswordHint />
        {signupOpen && (
          <div className="or">
            <p className="muted">Noch kein Konto?</p>
            <a className="button wide center" href="#/registrieren" style={{ display: "block" }}>
              Neuen Betrieb anlegen
            </a>
            <p className="muted">Wer zu einem Betrieb gehört, bekommt vom Inhaber eine Einladung.</p>
          </div>
        )}
      </div>
    </AuthPage>
  );
}

/**
 * „Passwort vergessen?“ (25.10.2026): user name or e-mail address, then always the same sentence,
 * whether the account exists or not. The link comes by mail, holds an hour; the old password stays
 * until it is used.
 */
function Forgot({ who: start, onBack }: { who: string; onBack: () => void }) {
  const [who, setWho] = useState(start);
  const [state, setState] = useState<{ sent?: boolean; error?: string; busy?: boolean }>({});
  async function submit(e: FormEvent) {
    e.preventDefault();
    if (!who.trim()) return setState({ error: "Bitte den Benutzernamen oder die E-Mail-Adresse angeben." });
    setState({ busy: true });
    try {
      await account.forgot(who.trim());
      setState({ sent: true });
    } catch (err) {
      setState({ error: messageOf(err) });
    }
  }
  return (
    <AuthPage>
      <div className="card" data-testid="forgot-card">
        <h2>Passwort vergessen</h2>
        {state.sent ? (
          <p className="ok-text" data-testid="forgot-sent">
            Wenn es das Konto gibt und seine E-Mail-Adresse bestätigt ist, ist ein Link unterwegs. Er gilt eine Stunde. Bis dahin bleibt das bisherige Passwort gültig. {SPAM_FOLDER}
          </p>
        ) : (
          <form noValidate onSubmit={submit}>
            <p className="hint">Wir schicken einen Link, mit dem du ein neues Passwort festlegst, an die bestätigte E-Mail-Adresse deines Kontos.</p>
            <label>
              Benutzername oder E-Mail-Adresse
              <input value={who} onChange={(e) => [setWho(e.target.value), setState({})]} autoComplete="username" autoCapitalize="none" autoCorrect="off" spellCheck={false} data-testid="forgot-who" />
            </label>
            {state.error && <p className="error-box">{state.error}</p>}
            <button className="primary" disabled={state.busy} data-testid="forgot-send">
              {state.busy ? "Wird geschickt …" : "Link schicken"}
            </button>
          </form>
        )}
        <p className="hint">Ohne bestätigte E-Mail-Adresse hilft der Inhaber des Betriebs: Er schickt unter Konto → Betrieb mit „Passwort zurücksetzen“ einen Link für ein neues Passwort.</p>
        <button className="link" onClick={onBack} data-testid="forgot-back">
          ← Zurück zur Anmeldung
        </button>
      </div>
    </AuthPage>
  );
}
